/**
 * Fit Finder — size quiz for a Shopify PDP.
 * Pair with snippets/fit-finder-modal.liquid + assets/fit-finder.css.
 *
 * Load as: {{ 'fit-finder.js' | asset_url | script_tag }}
 * Do NOT paste this into a text/rich-text field — it must be a real
 * asset file loaded via script_tag, or it will render as plain text.
 */
(function () {
  function sizeFromChest(chest, sizeOrder) {
    const c = parseFloat(chest);
    if (!c) return null;
    const breakpoints = [34, 37, 40, 42, 44];
    let idx = breakpoints.findIndex((b) => c <= b);
    if (idx === -1) idx = sizeOrder.length - 1;
    return sizeOrder[Math.min(idx, sizeOrder.length - 1)];
  }

  function sizeFromWeight(weight, sizeOrder) {
    const w = parseInt(weight, 10);
    const mid = Math.floor(sizeOrder.length / 2);
    if (!w) return sizeOrder[mid];
    const breakpoints = [130, 145, 160, 185, 200];
    let idx = breakpoints.findIndex((b) => w <= b);
    if (idx === -1) idx = sizeOrder.length - 1;
    return sizeOrder[Math.min(idx, sizeOrder.length - 1)];
  }

  const SHAPE_DELTA = { narrower: -1, flatter: -1, average: 0, broader: 1, rounder: 1 };

  const RESULT_COPY = {
    slim: (s) => `A size ${s} sized down for a closer, tailored line through the body.`,
    regular: (s) => `A size ${s} true to size — clean through the body with proper sleeve and body length.`,
    relaxed: (s) => `A size ${s} sized up for a roomier, more relaxed drape.`
  };

  const DEFAULT_STATE = {
    step: 1, feet: 5, inches: 7, weight: 160, chest: '',
    chestShape: 'average', stomachShape: 'average', fit: 'regular'
  };

  class FitFinder {
    constructor(script) {
      this.script = script;

      // this.root is the original wrapper (contains the trigger button).
      // this.modal gets relocated to <body> below, so it must be treated
      // as its own separate scope from this point on — everything that
      // lives INSIDE the modal (steppers, inputs, buttons, results) has
      // to be found via this.modal, not this.root, or the listeners
      // attach to nothing.
      this.root = script.closest('[data-fit-finder]') || script.parentElement || document;
      this.form = this.root.querySelector('form[action*="/cart/add"]')
        || script.closest('form')
        || document.querySelector('form[action*="/cart/add"]');
      this.modal = this.root.querySelector('[data-fit-finder-modal]');

      // Relocate the modal to be a direct child of <body>. This guarantees
      // position: fixed works correctly no matter what CSS an ancestor has
      // (sticky product-info columns, transforms, etc. can otherwise trap
      // a fixed-position descendant inside themselves instead of letting
      // it float over the whole viewport).
      if (this.modal && this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      try {
        this.variants = JSON.parse(script.textContent || '[]');
      } catch (e) {
        this.variants = [];
        console.warn('Fit Finder: variant JSON did not parse.', e);
      }

      this.sizeOrder = Array.from(new Set(this.variants.map((v) => v.size))).filter(Boolean);
      if (!this.sizeOrder.length) this.sizeOrder = ['S', 'M', 'M/L', 'L', 'XL'];

      this.state = Object.assign({}, DEFAULT_STATE);
      this.onKeydown = this.onKeydown.bind(this);
      this.bind();
    }

    // scope defaults to the modal (where almost everything lives now that
    // it's been moved to <body>). Pass this.root explicitly for anything
    // that's still part of the original wrapper (just the open trigger).
    on(selector, event, handler, scope) {
      const searchIn = scope || this.modal || this.root;
      const nodes = searchIn.querySelectorAll(selector);
      if (!nodes.length) {
        console.warn('Fit Finder: no element matched', selector);
        return;
      }
      nodes.forEach((el) => el.addEventListener(event, handler));
    }

    bind() {
      // The open trigger lives in the original wrapper, not the modal.
      this.on('[data-fit-finder-open]', 'click', (e) => { e.preventDefault(); this.open(); }, this.root);

      if (!this.modal) return;

      this.on('[data-fit-finder-close]', 'click', (e) => { e.preventDefault(); this.close(); });
      this.on('[data-fit-finder-backdrop]', 'click', () => this.close());

      this.modal.querySelectorAll('.fit-finder-stepper').forEach((stepper) => {
        const key = stepper.dataset.stepper;
        const min = parseInt(stepper.dataset.min, 10);
        const max = parseInt(stepper.dataset.max, 10);
        const step = parseInt(stepper.dataset.step || '1', 10);
        const dec = stepper.querySelector('[data-dec]');
        const inc = stepper.querySelector('[data-inc]');
        if (dec) dec.addEventListener('click', () => {
          this.state[key] = Math.max(min, this.state[key] - step);
          this.renderStepper(stepper, this.state[key]);
        });
        if (inc) inc.addEventListener('click', () => {
          this.state[key] = Math.min(max, this.state[key] + step);
          this.renderStepper(stepper, this.state[key]);
        });
      });

      this.on('[data-chest-input]', 'input', (e) => { this.state.chest = e.target.value; });

      this.modal.querySelectorAll('[data-choice-group]').forEach((group) => {
        const key = group.dataset.choiceGroup;
        group.querySelectorAll('[data-value]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.state[key] = btn.dataset.value;
            group.querySelectorAll('[data-value]').forEach((b) => b.removeAttribute('data-selected'));
            btn.setAttribute('data-selected', '');
          });
        });
      });

      this.on('[data-next]', 'click', () => this.goStep(this.state.step + 1));
      this.on('[data-back]', 'click', () => this.goStep(this.state.step - 1));
      this.on('[data-result]', 'click', () => { this.computeResult(); this.goStep(6); });
      this.on('[data-restart]', 'click', () => this.reset());
      this.on('[data-apply-size]', 'click', () => this.applySize());
    }

    onKeydown(e) {
      if (e.key === 'Escape') this.close();
    }

    renderStepper(stepper, value) {
      const out = stepper.querySelector('[data-value]');
      if (out) out.textContent = value;
    }

    goStep(n) {
      if (n < 1 || n > 6 || !this.modal) return;
      this.state.step = n;
      this.modal.querySelectorAll('.fit-finder-step').forEach((el) => {
        el.hidden = parseInt(el.dataset.step, 10) !== n;
      });
      const label = this.modal.querySelector('[data-fit-finder-step-label]');
      if (label) label.textContent = `Step ${n} of 6`;
      this.modal.scrollTop = 0;
    }

    computeResult() {
      if (!this.modal) return;
      const { chest, weight, chestShape, stomachShape, fit } = this.state;
      const sizeOrder = this.sizeOrder;
      const baseSize = sizeFromChest(chest, sizeOrder) || sizeFromWeight(weight, sizeOrder);
      let idx = sizeOrder.indexOf(baseSize);
      if (idx === -1) idx = Math.floor(sizeOrder.length / 2);

      const shapeSum = (SHAPE_DELTA[chestShape] || 0) + (SHAPE_DELTA[stomachShape] || 0);
      const netShape = shapeSum >= 2 ? 1 : shapeSum <= -2 ? -1 : 0;
      idx = Math.min(sizeOrder.length - 1, Math.max(0, idx + netShape));

      if (fit === 'slim' && idx > 0) idx -= 1;
      if (fit === 'relaxed' && idx < sizeOrder.length - 1) idx += 1;

      this.resultSize = sizeOrder[idx];
      const sizeEl = this.modal.querySelector('[data-result-size]');
      const copyEl = this.modal.querySelector('[data-result-copy]');
      const copyFn = RESULT_COPY[fit] || RESULT_COPY.regular;
      if (sizeEl) sizeEl.textContent = this.resultSize;
      if (copyEl) copyEl.textContent = copyFn(this.resultSize);
    }

    applySize() {
      const match = this.variants.find((v) => v.size === this.resultSize);
      if (!match) {
        console.warn('Fit Finder: no variant found for size', this.resultSize);
        this.close();
        return;
      }

      const form = this.form;
      if (form) {
        const select = form.querySelector('select[name="id"]');
        if (select) {
          select.value = match.id;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          this.close();
          return;
        }
        const radio = form.querySelector(`input[name="id"][value="${match.id}"]`);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          this.close();
          return;
        }
      }

      const scopeForLabels = form || document;
      const candidate = Array.from(scopeForLabels.querySelectorAll('label, button, span'))
        .find((el) => el.textContent.trim() === this.resultSize && el.closest('fieldset, .product-form, form'));
      if (candidate) candidate.click();

      this.close();
    }

    reset() {
      if (!this.modal) return;
      this.state = Object.assign({}, DEFAULT_STATE);
      const chestInput = this.modal.querySelector('[data-chest-input]');
      if (chestInput) chestInput.value = '';
      this.modal.querySelectorAll('.fit-finder-stepper').forEach((s) =>
        this.renderStepper(s, this.state[s.dataset.stepper])
      );
      this.modal.querySelectorAll('[data-choice-group]').forEach((group) => {
        group.querySelectorAll('[data-value]').forEach((b) => b.removeAttribute('data-selected'));
        const def = group.querySelector('[data-value="average"], [data-value="regular"]');
        if (def) def.setAttribute('data-selected', '');
      });
      this.goStep(1);
    }

    isOverlay() {
      if (!this.modal) return false;
      const pos = window.getComputedStyle(this.modal).position;
      return pos === 'fixed' || pos === 'absolute';
    }

    open() {
      if (!this.modal) return;
      this.modal.hidden = false;
      this.locked = this.isOverlay();
      if (this.locked) {
        this.scrollY = window.scrollY;
        document.body.style.overflow = 'hidden';
      }
      document.addEventListener('keydown', this.onKeydown);
      const firstClose = this.modal.querySelector('[data-fit-finder-close]');
      if (firstClose) firstClose.focus({ preventScroll: true });
    }

    close() {
      if (this.modal) this.modal.hidden = true;
      if (this.locked) {
        document.body.style.overflow = '';
        window.scrollTo(0, this.scrollY || 0);
        this.locked = false;
      }
      document.removeEventListener('keydown', this.onKeydown);
      const opener = this.root.querySelector('[data-fit-finder-open]');
      if (opener) opener.focus({ preventScroll: true });
    }
  }

  function init() {
    document.querySelectorAll('[data-fit-finder-variants]').forEach((script) => {
      new FitFinder(script);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();